import { QueryKey, UseQueryOptions } from "@tanstack/react-query";

import { graphql } from "lib/generated/app-service-gql";
import { BuyerDetailsByIdQuery, BuyerDetailsQuery } from "lib/generated/app-service-gql/graphql";
import { checkIfStringIsGuid } from "lib/utils/signalUtils";
import { useGraphQlQuery } from "../useGraphQlClient";
import { REACT_QUERY_OPTIONS_NEVER_REFETCH } from "../utils";

const BUYER_DETAILS_QUERY = graphql(`
  query buyerDetails($guids: [String!]) {
    buyers(BuyersRequest: { guids: $guids }) {
      results {
        id
        name
        guid
        categorySource
        categoryUni
        referenceCode
        createdAt
        updatedAt
        buyerUrl
        country
        countryCode
        town
        postalCode
        address
        cleanName
        nutsLocation
        categoryAssignments {
          categoryId
          categoryName
          parentCategoryId
          parentCategoryName
          source
        }
        oscarId
        logoUrl
        isStotlesVerified
        isArchived
        isInactive
        twitterHandle
        oscarCategory
        employeeCount
        annualSpend
        locationSummary
        url
        replacedByBuyers {
          id
          name
          guid
          logoUrl
        }
      }
      totalResults
    }
  }
`);

const BUYER_DETAILS_BY_ID_QUERY = graphql(`
  query buyerDetailsById($ids: [Int!]) {
    buyers(BuyersRequest: { ids: $ids }) {
      results {
        id
        name
        guid
        categorySource
        categoryUni
        referenceCode
        createdAt
        updatedAt
        buyerUrl
        country
        countryCode
        town
        postalCode
        address
        cleanName
        nutsLocation
        categoryAssignments {
          categoryId
          categoryName
          parentCategoryId
          parentCategoryName
          source
        }
        oscarId
        logoUrl
        isStotlesVerified
        isArchived
        isInactive
        twitterHandle
        oscarCategory
        employeeCount
        annualSpend
        locationSummary
        url
        replacedByBuyers {
          id
          name
          guid
          logoUrl
        }
      }
      totalResults
    }
  }
`);

export type BuyerFromQuery = BuyerDetailsQuery["buyers"]["results"][number];

/**
 * Returns detailed information about a buyer
 * @param buyerGuid The GUID of the buyer to fetch
 * @param options Optional React Query options
 */
export function useBuyerDetailsGql(
  buyerGuid: string,
  options?: UseQueryOptions<BuyerDetailsQuery, unknown, BuyerDetailsQuery, QueryKey>,
) {
  const { data, ...rest } = useGraphQlQuery(
    ["buyerDetails", buyerGuid],
    BUYER_DETAILS_QUERY,
    [{ guids: [buyerGuid] }],
    {
      ...REACT_QUERY_OPTIONS_NEVER_REFETCH,
      ...options,
    },
  );

  return {
    data: data?.buyers.results,
    ...rest,
  };
}

/**
 * Returns detailed information about a buyer by numeric ID.
 * Used by TanStack Router buyer pages where the URL contains numeric IDs.
 */
export function useBuyerDetailsByNumericId(
  buyerId: number,
  options?: UseQueryOptions<BuyerDetailsByIdQuery, unknown, BuyerDetailsByIdQuery, QueryKey>,
) {
  const { data, ...rest } = useGraphQlQuery(
    ["buyerDetails", "id", buyerId],
    BUYER_DETAILS_BY_ID_QUERY,
    [{ ids: [buyerId] }],
    {
      ...REACT_QUERY_OPTIONS_NEVER_REFETCH,
      ...options,
    },
  );

  return {
    data: data?.buyers.results,
    ...rest,
  };
}

/**
 * Single entry point for "fetch the buyer behind this URL", regardless of whether the URL
 * carries a guid or a numeric id. Mirrors `useSupplierLookup`.
 */
export function useBuyerLookup(idOrGuid: string): {
  data: BuyerFromQuery[] | undefined;
  isLoading: boolean;
} {
  const isGuid = checkIfStringIsGuid(idOrGuid);
  const numericId = parseInt(idOrGuid, 10);

  const guidQuery = useBuyerDetailsGql(isGuid ? idOrGuid : "", { enabled: isGuid });
  const numericQuery = useBuyerDetailsByNumericId(isGuid || isNaN(numericId) ? 0 : numericId, {
    enabled: !isGuid && !isNaN(numericId) && numericId > 0,
  });

  if (isGuid) {
    return { data: guidQuery.data, isLoading: guidQuery.isLoading };
  }
  return { data: numericQuery.data, isLoading: numericQuery.isLoading };
}
